import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
    Avatar,
    Badge,
    Box,
    // Button,
    // CardActions,
    Chip,
    ClickAwayListener,
    Divider,
    Grid,
    Paper,
    Popper,
    Stack,
    TextField,
    Typography,
    useMediaQuery
} from '@mui/material';

// third-party
import PerfectScrollbar from 'react-perfect-scrollbar';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import Transitions from 'ui-component/extended/Transitions';
import NotificationList from './NotificationList';

// assets
import { IconBell } from '@tabler/icons-react';
import { useDispatch } from 'store';
import { useSelector } from 'store';
import { fetchNotifications, setIsFetchingNotifications, setParams, setUnreadCount } from 'store/slices/notifications';
import { markReadNotifications } from 'api/notifications';
import { ModalContext } from 'contexts/ModalContext';
import CircularLoader from 'ui-component/CircularLoader';

// notification status options
const status = [
    {
        value: 'all',
        label: 'All Notification'
    },
    {
        value: 'read',
        label: 'Read'
    },
    {
        value: 'unread',
        label: 'Unread'
    }
    // {
    //     value: 'other',
    //     label: 'Other'
    // }
];

// ==============================|| NOTIFICATION ||============================== //

const NotificationSection = () => {
    const theme = useTheme();
    const matchesXs = useMediaQuery(theme.breakpoints.down('md'));
    const dispatch = useDispatch();
    const [notificationList, setNotificationList] = useState([]); // this will handle the notification list in the page
    const { notifications, notificationsCount, params, unreadCount, isFetchingNotifications } = useSelector((state) => state.notifications);
    const [open, setOpen] = useState(false);
    const prevOpen = useRef(open);
    const { setOpenInfoModal, setOpenConfirmationModal } = useContext(ModalContext);
    const [isProceedMarkAllRead, setIsProceedMarkAllRead] = useState(false);
    const containerRef = useRef(null);
    const [isScrollToBottom, setIsScrollToBottom] = useState(false);
    const [maxHeight, setMaxHeight] = useState('400px'); // for detection in PerfectScrollbar

    useEffect(() => {
        const handleResize = () => {
            const width = window.innerWidth;
            if (width >= 1200) {
                setMaxHeight('540px'); // xl breakpoint
            } else {
                setMaxHeight('400px'); // xs breakpoint
            }
        };

        handleResize(); // initial check
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    /**
     * anchorRef is used on different componets and specifying one type leads to other components throwing an error
     * */
    const anchorRef = useRef(null);

    const handleToggle = async () => {
        let notificationIds = [];
        for (let notification of notifications) {
            notificationIds.push(notification._id);
        }

        if (notificationIds.length > 0) {
            processMarkReadNotifications({
                notificationIds
            });
        }

        setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }
        setOpen(false);
    };

    const getNotifications = useCallback(
        async (params, isReset = true) => {
            try {
                if (isReset) {
                    await dispatch(setIsFetchingNotifications(true));
                }
                const list = await dispatch(fetchNotifications(params, isReset));
                if (isReset) {
                    setNotificationList(list);
                } else {
                    if (list.length > 0) {
                        setNotificationList([...notificationList, ...list]);

                        let notificationIds = [];
                        for (let notification of list) {
                            notificationIds.push(notification._id);
                        }
                        if (notificationIds.length > 0) {
                            processMarkReadNotifications({
                                notificationIds
                            });
                        }
                    }
                }
            } catch (e) {
                console.log('e', e);
            } finally {
                if (isReset) {
                    await dispatch(setIsFetchingNotifications(false));
                }
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [dispatch, notificationList]
    );

    useEffect(() => {
        const isReset = params.pageNo > 1 ? false : true;
        getNotifications(params, isReset);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params]);

    useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current.focus();
        }
        prevOpen.current = open;
    }, [open]);

    const handleFilterStatus = (event) => {
        dispatch(
            setParams({
                ...params,
                showType: event.target.value,
                pageNo: 1
            })
        );
    };

    const handleMarkAllRead = async () => {
        setOpenConfirmationModal({
            isOpen: true,
            title: 'Are you sure you want to mark all as read?',
            isProceed: isProceedMarkAllRead,
            handleProceed: async () => {
                try {
                    processMarkReadNotifications({
                        markAllRead: true
                    });
                    setOpenInfoModal({
                        isOpen: true,
                        type: 'success',
                        title: 'Notifications mark read successfully'
                    });
                } catch (error) {
                    setOpenInfoModal({
                        isOpen: true,
                        type: 'error',
                        title: 'Notifications mark read error'
                    });
                } finally {
                    setIsProceedMarkAllRead(false);
                    setOpenConfirmationModal({
                        isOpen: false
                    });
                }
            }
        });
    };

    const processMarkReadNotifications = async ({ notificationIds = [], markAllRead = false }) => {
        let readParams = {
            currentDateTime: params.currentDateTime
        };

        if (markAllRead) {
            readParams = {
                ...readParams,
                markAllRead
            };
        } else {
            readParams = {
                ...readParams,
                notificationIds
            };
        }

        const response = await markReadNotifications(readParams);

        if (response.success) {
            const { data } = response;
            const {
                summary: { unreadCount = 0 }
            } = data;
            await dispatch(setUnreadCount(unreadCount));
        }
    };

    const handleScroll = async () => {
        try {
            const container = containerRef.current;

            if (container && notificationList.length !== notificationsCount) {
                const { scrollTop, scrollHeight, clientHeight } = container;

                // Check if the user has scrolled to the bottom
                if (scrollTop + clientHeight >= scrollHeight || scrollHeight - (scrollTop + clientHeight) <= 0.99) {
                    setIsScrollToBottom(true);
                    dispatch(
                        setParams({
                            ...params,
                            pageNo: params.pageNo + 1
                        })
                    );
                }
            }
        } catch (err) {
            console.log('err', err);
        } finally {
            setIsScrollToBottom(false);
        }
    };

    return (
        <>
            <Box
                sx={{
                    ml: 2,
                    [theme.breakpoints.down('lg')]: {
                        mr: 2
                    }
                }}
            >
                <Badge
                    badgeContent={unreadCount} // Display the unread count
                    color="error" // Badge color (red for unread)
                    invisible={unreadCount < 1}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right'
                    }}
                >
                    <Avatar
                        variant="rounded"
                        sx={{
                            ...theme.typography.commonAvatar,
                            ...theme.typography.mediumAvatar,
                            transition: 'all .2s ease-in-out',
                            background: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.secondary.light,
                            color: theme.palette.mode === 'dark' ? theme.palette.warning.dark : theme.palette.secondary.dark,
                            '&[aria-controls="menu-list-grow"],&:hover': {
                                background: theme.palette.mode === 'dark' ? theme.palette.warning.dark : theme.palette.secondary.dark,
                                color: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.secondary.light
                            }
                        }}
                        ref={anchorRef}
                        aria-controls={open ? 'menu-list-grow' : undefined}
                        aria-haspopup="true"
                        onClick={handleToggle}
                        color="inherit"
                    >
                        <IconBell stroke={1.5} size="20px" />
                    </Avatar>
                </Badge>
            </Box>

            <Popper
                placement={matchesXs ? 'bottom' : 'bottom-end'}
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                modifiers={[
                    {
                        name: 'offset',
                        options: {
                            offset: [matchesXs ? 5 : 0, 20]
                        }
                    }
                ]}
            >
                {({ TransitionProps }) => (
                    <ClickAwayListener onClickAway={handleClose}>
                        <Transitions position={matchesXs ? 'top' : 'top-right'} in={open} {...TransitionProps}>
                            <Paper sx={{ minWidth: { xs: '300px', md: '330px', overflow: 'hidden' } }}>
                                {open && (
                                    <MainCard border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]}>
                                        <Grid container direction="column" spacing={2}>
                                            <Grid item xs={12}>
                                                <Grid container alignItems="center" justifyContent="space-between" sx={{ pt: 2, px: 2 }}>
                                                    <Grid item>
                                                        <Stack direction="row" spacing={2}>
                                                            <Typography variant="subtitle1">All Notification</Typography>
                                                            <Chip
                                                                size="small"
                                                                label={notificationsCount}
                                                                sx={{
                                                                    color: theme.palette.background.default,
                                                                    bgcolor: theme.palette.warning.dark
                                                                }}
                                                            />
                                                        </Stack>
                                                    </Grid>
                                                    {unreadCount > 0 && (
                                                        <Grid item>
                                                            <Typography
                                                                component={Link}
                                                                to="#"
                                                                variant="subtitle2"
                                                                color="primary"
                                                                onClick={handleMarkAllRead}
                                                            >
                                                                Mark as all read
                                                            </Typography>
                                                        </Grid>
                                                    )}
                                                </Grid>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Grid container direction="column" spacing={2}>
                                                    <Grid item xs={12}>
                                                        <Box sx={{ px: 2, pt: 0.25 }}>
                                                            <TextField
                                                                id="outlined-select-currency-native"
                                                                select
                                                                fullWidth
                                                                value={params?.showType || 'all'}
                                                                onChange={handleFilterStatus}
                                                                SelectProps={{
                                                                    native: true
                                                                }}
                                                            >
                                                                {status.map((option) => (
                                                                    <option key={option.value} value={option.value}>
                                                                        {option.label}
                                                                    </option>
                                                                ))}
                                                            </TextField>
                                                        </Box>
                                                    </Grid>
                                                    <Grid item xs={12} p={0}>
                                                        <Divider sx={{ my: 0 }} />
                                                    </Grid>
                                                </Grid>
                                                <PerfectScrollbar
                                                    containerRef={(el) => (containerRef.current = el)} // Assign the container ref
                                                    onScrollY={handleScroll}
                                                    style={{
                                                        height: '100%',
                                                        maxHeight,
                                                        overflowX: 'hidden',
                                                        overflowY: 'auto'
                                                    }}
                                                >
                                                    {isFetchingNotifications && <CircularLoader size={24} />}
                                                    {!isFetchingNotifications && <NotificationList notificationList={notificationList} />}
                                                    {isScrollToBottom && (
                                                        <Box>
                                                            <CircularLoader size={24} maxHeight="0rem" />
                                                        </Box>
                                                    )}
                                                    {!isFetchingNotifications && notificationsCount === 0 && (
                                                        <Box display="flex" sx={{ justifyContent: 'center', p: 2 }}>
                                                            No Results
                                                        </Box>
                                                    )}
                                                </PerfectScrollbar>
                                            </Grid>
                                        </Grid>
                                        {/* <Divider />
                                        <CardActions sx={{ p: 1.25, justifyContent: 'center' }}>
                                            <Button size="small" disableElevation>
                                                View All
                                            </Button>
                                        </CardActions> */}
                                    </MainCard>
                                )}
                            </Paper>
                        </Transitions>
                    </ClickAwayListener>
                )}
            </Popper>
        </>
    );
};

export default NotificationSection;
